import { isVisibleComponetBaseOnRole } from "@/commons/RoleFunction.ts";
import { IOption, EAlignment, EPosition } from "@/dependencies";
import {
  Field,
  FieldProps,
  makeStyles,
  mergeClasses,
  Option,
  Popover,
  PopoverSurface,
  PositioningImperativeRef,
  useRestoreFocusTarget
} from "@fluentui/react-components";
import { Input } from "@/dependencies";
import { ChevronDownRegular } from "@fluentui/react-icons";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { OptionWrapper, TimePickerWrapper } from "./Style";
import { format, isValid } from "date-fns";

const useStyles = makeStyles({
  root: {
    fontWeight: 600
  }
});

interface ITimePickerProps {
  rcName?: string;
  theme?: string;
  onChangeText?: (value: string) => void;
  rcRole?: string[];
  requireRoles?: string[];
  disabled?: boolean;
  placeholder?: string;
  format?: string;
  value?: string;
  options?: IOption[];
  onSelectedTime?: (value: string) => void;
}

const TimePickerCustom = (props: ITimePickerProps & FieldProps) => {
  const styles = useStyles();
  const isCanBeVisible = isVisibleComponetBaseOnRole(props.rcRole, props.requireRoles);

  const wrapperRef = useRef<HTMLInputElement | null>(null);
  const positioningRef = useRef<PositioningImperativeRef>(null);
  const restoreFocusTargetAttribute = useRestoreFocusTarget();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState<string>("");
  const headerId = useId();

  useEffect(() => {
    if (wrapperRef?.current && positioningRef.current) {
      positioningRef.current?.setTarget(wrapperRef.current);
    }
  }, [wrapperRef, positioningRef]);

  useEffect(() => {
    if (props.value) {
      setValue(props.value);
    }
  }, [props.value]);

  const optionsRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      requestAnimationFrame(() => {
        const wrapper = optionsRef.current;
        const selectedEl = wrapper?.querySelector('.selected') as HTMLElement | null;
        if (selectedEl && wrapper) {
          selectedEl.scrollIntoView({ block: 'center', inline: 'nearest', behavior: 'auto' });
        }
      });
    }
  }, [open, value]);

  const getWidthElement = (): number => {
    const el = document.getElementById(`select_context_${props.rcName || "input"}`);
    return el?.offsetWidth ? el.offsetWidth : 0;
  };

  const validateTime = (timeValue: string) => {
    if (!timeValue || !/^\d{1,2}:\d{2}$/.test(timeValue)) {
      return false;
    }
    const date = new Date(`2025-01-01T${timeValue}:00`);
    return isValid(date);
  };

  const handleTimeValidation = (timeValue: string) => {
    if (!validateTime(timeValue)) {
      console.warn("Invalid time format. Please use format:", props.format || "HH:mm");
      setValue("00:00");
      if (props.onChangeText) {
        props.onChangeText("00:00");
      }
      if (props.onSelectedTime) {
        props.onSelectedTime("00:00");
      }
      setOpen(false);
    } else {
      if (props.onSelectedTime) {
        props.onSelectedTime(timeValue);
      }
    }
  };

  const onChange = (_event: React.ChangeEvent<HTMLInputElement>, newValue?: string) => {
    setValue(newValue || "");
    if (props.onChangeText) {
      props.onChangeText(newValue || "");
    }
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      handleTimeValidation(value);
      setOpen(false);
    }
  };

  const onBlur = () => {
    handleTimeValidation(value);
  };

  const displayValue = useMemo(() => {
    const valueClone = value;
    if (validateTime(valueClone)) {
      try {
        const displayValue = format(
          new Date(`2025-01-01T${valueClone}:00`),
          props.format || "h:mm"
        );
        return displayValue;
      } catch {
        return format(new Date(`2025-01-01T00:00:00`), props.format || "h:mm");
      }
    }
    return valueClone;
  }, [value]);

  return (
    <TimePickerWrapper
      className={mergeClasses(props.className, "TimePickerWrapper")}
      ref={wrapperRef}
      id={`select_context_${props.rcName || "input"}`}
    >
      <Field
        label={props.label}
        validationMessage={props.validationMessage}
        validationMessageIcon={props.validationMessageIcon}
        validationState={props.validationState}
        required={props.required}
        style={props.style}
        className={styles.root}
      >
        <Input
          {...restoreFocusTargetAttribute}
          onClick={() => setOpen((s) => !s)}
          style={{ width: "100%", cursor: "pointer" }}
          disabled={props.disabled || !isCanBeVisible}
          value={displayValue}
          onChangeText={onChange}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          contentAfter={ props.disabled || !isCanBeVisible ? null : <ChevronDownRegular onClick={() => setOpen((s) => !s)} />}
        />
      </Field>
      <Popover
        onOpenChange={(_, data) => setOpen(data.open)}
        trapFocus
        open={open}
        positioning={{
          positioningRef,
          position: EPosition.BELOW,
          align: EAlignment.START
        }}
        mountNode={undefined}
      >
        <PopoverSurface
          aria-labelledby={headerId}
          className='width_custom_popover scroll-bar-custom'
          style={{
            maxHeight: "400px",
            overflow: "auto",
            width: getWidthElement(),
            padding: "4px"
          }}
        >
          <OptionWrapper className='OptionWrapper custom-scrollbar' ref={optionsRef}>
            {(props.options || [])?.map((option) => (
              <Option
                key={option.value}
                value={option.value}
                text=''
                onClick={() => {
                  setValue(option.value);
                  if (props.onChangeText) {
                    props.onChangeText(option.value);
                  }
                  if (props.onSelectedTime) {
                    props.onSelectedTime(option.value);
                  }
                  setOpen(false);
                }}
                className={value === option.value ? "selected" : ""}
              >
                {option.text}
              </Option>
            ))}
          </OptionWrapper>
        </PopoverSurface>
      </Popover>
    </TimePickerWrapper>
  );
};

export default TimePickerCustom;
